<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/logbook/chemicals/templates">Chemical Templates</a></li>
        <li class="breadcrumb-item active">{{ isEditMode ? 'Edit Template' : 'New Template' }}</li>
      </ol>
    </nav>
    <h1 class="h2 mb-0 fw-bold">{{ isEditMode ? 'Edit Chemical Template' : 'New Chemical Template' }}</h1>
  </div>
</div>

<form [formGroup]="form" (ngSubmit)="onSubmit()">
  <div class="row g-4">
    <!-- Left Column -->
    <div class="col-lg-8">
      <!-- Basic Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Basic Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">Template Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control" formControlName="name"
                     placeholder="e.g., Corn Pre-Emergence Standard"
                     [class.is-invalid]="isFieldInvalid('name')">
              <div class="invalid-feedback">Name is required (minimum 3 characters)</div>
            </div>
            <div class="col-12">
              <label class="form-label">Description <span class="text-danger">*</span></label>
              <textarea appCharCounter class="form-control" formControlName="description" rows="2"
                        placeholder="Brief description of what this template is for"
                        [class.is-invalid]="isFieldInvalid('description')"></textarea>
              <div class="invalid-feedback">Description is required</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chemicals -->
      <div class="card mb-4">
        <div class="card-header p-3 d-flex justify-content-between align-items-center">
          <h5 class="mb-0"><i class="bi bi-droplet-fill me-2"></i>Chemicals in Template</h5>
          <div class="d-flex gap-2">
            <button type="button" class="btn btn-sm btn-outline-primary" (click)="activeChemicalIndex = null; showChemicalLookup = true; filterLookupChemicals()">
              <i class="bi bi-search me-1"></i>Look Up Chemical
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="addChemical()">
              <i class="bi bi-plus-lg me-1"></i>Add Manual
            </button>
          </div>
        </div>
        <div class="card-body">
          <p class="text-muted small mb-3">
            Add chemicals from EPA, Acre Advantage, or your custom chemicals. <span class="text-danger">*</span>
          </p>

          <div class="chemicals-list" formArrayName="chemicals">
            <div *ngFor="let chemical of chemicals.controls; let i = index; let first = first; let last = last"
                 class="chemical-item mb-3 p-3 rounded"
                 [formGroupName]="i">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div class="d-flex align-items-center gap-2">
                  <span class="badge bg-secondary">{{ i + 1 }}</span>
                  <span *ngIf="chemical.get('source')?.value && chemical.get('source')?.value !== 'manual'"
                        class="badge" [ngClass]="getSourceBadgeClass(chemical.get('source')?.value)">
                    {{ getSourceLabel(chemical.get('source')?.value) }}
                  </span>
                </div>
                <div class="d-flex gap-1">
                  <button type="button" class="btn btn-sm btn-ghost" (click)="openChemicalLookup(i)"
                          title="Look up chemical">
                    <i class="bi bi-search"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-ghost" (click)="moveChemicalUp(i)"
                          [disabled]="first" title="Move up">
                    <i class="bi bi-chevron-up"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-ghost" (click)="moveChemicalDown(i)"
                          [disabled]="last" title="Move down">
                    <i class="bi bi-chevron-down"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-ghost text-danger" (click)="removeChemical(i)"
                          title="Remove">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>
              <div class="row g-2">
                <div class="col-md-4">
                  <label class="form-label small">Chemical Name <span class="text-danger">*</span></label>
                  <input type="text" class="form-control form-control-sm" formControlName="name"
                         placeholder="e.g., Roundup PowerMax"
                         [class.is-invalid]="isChemicalFieldInvalid(i, 'name')">
                </div>
                <div class="col-md-4">
                  <label class="form-label small">Application Rate <span class="text-danger">*</span></label>
                    <div class="input-group input-group-sm">
                      <input type="number" class="form-control form-control-sm" formControlName="rateValue"
                             min="0" step="0.01"
                             placeholder="Rate Value"
                             [class.is-invalid]="isChemicalFieldInvalid(i, 'rateValue')">
                      <select class="form-select form-select-sm flex-grow-0 flex-shrink-0 w-auto" formControlName="rateUnit"
                              [class.is-invalid]="isChemicalFieldInvalid(i, 'rateUnit')">
                        <option [ngValue]="null" disabled>Rate Type</option>
                        <option *ngFor="let unit of rateUnitOptions" [ngValue]="unit.value">{{ unit.label }}</option>
                      </select>
                    </div>
                  </div>
                <div class="col-md-4">
                  <label class="form-label small">Purpose</label>
                  <input type="text" class="form-control form-control-sm" formControlName="purpose"
                         placeholder="e.g., Broadleaf control">
                </div>
              </div>
            </div>
          </div>

          <div *ngIf="chemicals.length === 0" class="text-center py-4">
            <div class="empty-state-icon mx-auto mb-3">
              <i class="bi bi-droplet"></i>
            </div>
            <p class="text-muted mb-3">No chemicals added yet</p>
            <div class="d-flex gap-2 justify-content-center">
              <button type="button" class="btn btn-primary btn-sm" (click)="activeChemicalIndex = null; showChemicalLookup = true; filterLookupChemicals()">
                <i class="bi bi-search me-1"></i>Look Up Chemical
              </button>
              <button type="button" class="btn btn-outline-secondary btn-sm" (click)="addChemical()">
                <i class="bi bi-plus-lg me-1"></i>Add Manually
              </button>
            </div>
          </div>

          <div *ngIf="chemicals.length === 0" class="alert alert-warning mt-3 mb-0 py-2">
            <i class="bi bi-exclamation-triangle me-2"></i>
            <span class="small">At least one chemical is required</span>
          </div>
        </div>
      </div>

      <!-- Target Crops -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-flower1 me-2"></i>Target Crops</h5>
        </div>
        <div class="card-body">
          <p class="text-muted small mb-3">
            Select the crops this template applies to. <span class="text-danger">*</span>
          </p>

          <!-- Selected crops display -->
          <div class="selected-items mb-3" *ngIf="selectedCrops.length > 0">
            <label class="form-label small text-muted">Selected crops:</label>
            <div class="d-flex flex-wrap gap-2">
              <span *ngFor="let crop of selectedCrops" class="badge selected-badge d-flex align-items-center gap-1">
                {{ crop }}
                <button type="button" class="btn-close btn-close-sm" (click)="removeCrop(crop)"></button>
              </span>
            </div>
          </div>

          <!-- Quick actions -->
          <div class="mb-3 d-flex gap-2">
            <button type="button" class="btn btn-sm btn-outline-primary" (click)="selectAllCrops()"
                    *ngIf="selectedCrops.length < cropOptions.length">
              Select All
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="clearCrops()"
                    *ngIf="selectedCrops.length > 0">
              Clear selection
            </button>
          </div>

          <!-- Crop options grid -->
          <div class="options-grid">
            <div *ngFor="let crop of cropOptions" class="form-check option-item clickable-option"
                 [class.selected]="isCropSelected(crop.value)"
                 (click)="toggleCrop(crop.value)">
              <input class="form-check-input pe-none" type="checkbox"
                     [checked]="isCropSelected(crop.value)">
              <span class="form-check-label">{{ crop.label }}</span>
            </div>
          </div>

          <!-- Add custom crop -->
          <div class="mt-3">
            <label class="form-label small text-muted">Add a crop not in the list:</label>
            <div class="d-flex gap-2">
              <input type="text" class="form-control form-control-sm" [(ngModel)]="newCropInput"
                     [ngModelOptions]="{standalone: true}"
                     placeholder="Enter crop name"
                     (keyup.enter)="addCustomCrop()">
              <button type="button" class="btn btn-sm btn-outline-primary" (click)="addCustomCrop()"
                      [disabled]="!newCropInput.trim()">
                Add
              </button>
            </div>
          </div>

          <div *ngIf="selectedCrops.length === 0" class="alert alert-warning mt-3 mb-0 py-2">
            <i class="bi bi-exclamation-triangle me-2"></i>
            <span class="small">Please select at least one target crop</span>
          </div>
        </div>
      </div>

      <!-- Target Pests (optional) -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-bug me-2"></i>Target Pests <span class="text-muted fw-normal small">(optional)</span></h5>
        </div>
        <div class="card-body">
          <p class="text-muted small mb-3">
            Select the pests, weeds, or diseases this template targets.
          </p>

          <!-- Selected pests display -->
          <div class="selected-items mb-3" *ngIf="selectedPests.length > 0">
            <label class="form-label small text-muted">Selected targets:</label>
            <div class="d-flex flex-wrap gap-2">
              <span *ngFor="let pest of selectedPests" class="badge selected-badge pest-badge d-flex align-items-center gap-1">
                {{ pest }}
                <button type="button" class="btn-close btn-close-sm" (click)="removePest(pest)"></button>
              </span>
            </div>
          </div>

          <!-- Quick action -->
          <div class="mb-3" *ngIf="selectedPests.length > 0">
            <button type="button" class="btn btn-sm btn-outline-secondary" (click)="clearPests()">
              Clear selection
            </button>
          </div>

          <!-- Pest options grid -->
          <div class="options-grid">
            <div *ngFor="let pest of pestOptions" class="form-check option-item clickable-option"
                 [class.selected]="isPestSelected(pest)"
                 (click)="togglePest(pest)">
              <input class="form-check-input pe-none" type="checkbox"
                     [checked]="isPestSelected(pest)">
              <span class="form-check-label">{{ pest }}</span>
            </div>
          </div>

          <!-- Add custom pest -->
          <div class="mt-3">
            <label class="form-label small text-muted">Add a target not in the list:</label>
            <div class="d-flex gap-2">
              <input type="text" class="form-control form-control-sm" [(ngModel)]="newPestInput"
                     [ngModelOptions]="{standalone: true}"
                     placeholder="Enter pest, weed, or disease name"
                     (keyup.enter)="addCustomPest()">
              <button type="button" class="btn btn-sm btn-outline-primary" (click)="addCustomPest()"
                      [disabled]="!newPestInput.trim()">
                Add
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Application Details -->
      <div class="card mb-4 mb-lg-0">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-calendar-event me-2"></i>Application Details</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Application Timing</label>
              <select class="form-select" formControlName="applicationTiming">
                <option value="">Select timing...</option>
                <option *ngFor="let timing of applicationTimings" [value]="timing.value">
                  {{ timing.label }}
                </option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label">Total Spray Volume <span class="text-danger">*</span></label>
              <div class="input-group">
                <input type="number" class="form-control" formControlName="totalVolume"
                       placeholder="e.g., 10"
                       [class.is-invalid]="isFieldInvalid('totalVolume')">
                <select class="form-select mw-120px" formControlName="volumeUnit">
                  <option *ngFor="let unit of volumeUnits" [value]="unit">{{ unit }}</option>
                </select>
              </div>
              <div class="invalid-feedback" *ngIf="isFieldInvalid('totalVolume')">Total volume is required</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Estimated Cost per Acre</label>
              <div class="input-group">
                <span class="input-group-text">$</span>
                <input type="number" class="form-control" formControlName="estimatedCostPerAcre"
                       placeholder="0.00" step="0.01" min="0">
              </div>
              <div class="form-text">Optional - helps with budgeting</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column - Sidebar -->
    <div class="col-lg-4">
      <div class="sticky-top sticky-sidebar">
      <!-- Tips Card -->
      <div class="card mb-4">
        <div class="card-body">
          <h6 class="d-flex align-items-center gap-2 mb-3">
            <i class="bi bi-lightbulb text-warning"></i>
            Tips
          </h6>
          <ul class="small text-muted mb-0 ps-3">
            <li class="mb-2">Templates save time when creating spray records</li>
            <li class="mb-2">Include all chemicals typically applied together</li>
            <li class="mb-2">Specify application rates for each chemical</li>
            <li class="mb-2">Add target pests to help identify the right template</li>
            <li>Track costs to improve budgeting accuracy</li>
          </ul>
        </div>
      </div>

      <!-- Summary Card (sticky) -->
      <div class="card summary-card">
        <div class="card-header p-3">
          <h5 class="mb-0">Template Summary</h5>
        </div>
        <div class="card-body">
          <div class="summary-item" *ngIf="form.get('name')?.value">
            <div class="summary-label text-muted">Name</div>
            <div class="summary-value">{{ form.get('name')?.value }}</div>
          </div>
          <div class="summary-item" *ngIf="chemicals.length > 0">
            <div class="summary-label text-muted">Chemicals</div>
            <div class="summary-value">{{ chemicals.length }} product{{ chemicals.length > 1 ? 's' : '' }}</div>
          </div>
          <div class="summary-item" *ngIf="getFullVolume()">
            <div class="summary-label text-muted">Volume</div>
            <div class="summary-value">{{ getFullVolume() }}</div>
          </div>
          <div class="summary-item" *ngIf="selectedCrops.length > 0">
            <div class="summary-label text-muted">Target Crops</div>
            <div class="summary-value">{{ selectedCrops.join(', ') }}</div>
          </div>
          <div class="summary-item" *ngIf="selectedPests.length > 0">
            <div class="summary-label text-muted">Target Pests</div>
            <div class="summary-value">{{ selectedPests.length }} selected</div>
          </div>
          <div class="summary-item" *ngIf="form.get('estimatedCostPerAcre')?.value">
            <div class="summary-label text-muted">Est. Cost/Acre</div>
            <div class="summary-value">${{ form.get('estimatedCostPerAcre')?.value | number:'1.2-2' }}</div>
          </div>

          <hr class="my-3" *ngIf="form.get('name')?.value || chemicals.length > 0">

          <!-- Additional Notes -->
          <div class="mb-3">
            <label class="form-label small text-muted">Notes</label>
            <textarea appCharCounter class="form-control form-control-sm" formControlName="notes" rows="2"
                      placeholder="e.g., Apply within 3 days of planting"></textarea>
          </div>

          <hr class="my-3">

          <!-- Action Buttons -->
          <div class="d-grid gap-2">
            <button type="submit" class="btn btn-primary" [disabled]="isSaving">
              <ng-container *ngIf="!isSaving">
                <i class="bi bi-check-lg me-2"></i>{{ isEditMode ? 'Update Template' : 'Save Template' }}
              </ng-container>
              <ng-container *ngIf="isSaving">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Saving...
              </ng-container>
            </button>
            <button type="button" class="btn btn-outline-secondary" (click)="onCancel()">
              Cancel
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</form>

<!-- Chemical Lookup Modal -->
<div class="modal-backdrop" *ngIf="showChemicalLookup" (click)="closeChemicalLookup()"></div>
<div class="chemical-lookup-modal" *ngIf="showChemicalLookup">
  <div class="modal-header">
    <h5 class="mb-0">
      <i class="bi bi-search me-2"></i>
      {{ activeChemicalIndex !== null ? 'Replace Chemical' : 'Add Chemical' }}
    </h5>
    <button type="button" class="btn-close" (click)="closeChemicalLookup()"></button>
  </div>

  <div class="modal-body">
    <!-- Search and Filter -->
    <div class="search-filters mb-3">
      <div class="input-group mb-3">
        <span class="input-group-text"><i class="bi bi-search"></i></span>
        <input type="text" class="form-control" placeholder="Search by name, active ingredient, manufacturer..."
               [(ngModel)]="chemicalSearchQuery" (ngModelChange)="filterLookupChemicals()">
        <button *ngIf="chemicalSearchQuery" class="btn btn-outline-secondary" type="button"
                (click)="chemicalSearchQuery = ''; filterLookupChemicals()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <!-- Source filter: Segmented Control -->
      <div class="lookup-segmented-control">
        <button type="button" class="lookup-segment" [class.active]="chemicalSourceFilter === 'custom'"
                (click)="chemicalSourceFilter = 'custom'; filterLookupChemicals()">
          <i class="bi bi-person me-1"></i>My Chemicals
          <span *ngIf="customChemicalsLoaded.length > 0" class="lookup-segment-count">{{ customChemicalsLoaded.length }}</span>
        </button>
        <button type="button" class="lookup-segment" [class.active]="chemicalSourceFilter === 'adn-recommended'"
                (click)="chemicalSourceFilter = 'adn-recommended'; filterLookupChemicals()">
          <i class="bi bi-patch-check-fill me-1"></i>Acre Advantage
          <span *ngIf="adnChemicalsLoaded.length > 0" class="lookup-segment-count">{{ adnChemicalsLoaded.length }}</span>
        </button>
        <button type="button" class="lookup-segment" [class.active]="chemicalSourceFilter === 'epa'"
                (click)="chemicalSourceFilter = 'epa'; filterLookupChemicals()">
          <i class="bi bi-shield-check me-1"></i>EPA
        </button>
      </div>
    </div>

    <!-- Results -->
    <div class="lookup-results">
      <!-- Loading spinner for EPA server search -->
      <div *ngIf="isSearchingLookup" class="text-center py-4">
        <span class="spinner-border spinner-border-sm me-2" role="status"></span>
        <span class="text-muted">Searching...</span>
      </div>

      <!-- EPA tab: prompt to type when no query -->
      <div *ngIf="!isSearchingLookup && chemicalSourceFilter === 'epa' && !chemicalSearchQuery.trim()" class="text-center py-4">
        <i class="bi bi-search text-muted fs-2rem"></i>
        <p class="text-muted mt-2 mb-0">Type to search EPA registered chemicals</p>
      </div>

      <!-- Custom tab: no chemicals message -->
      <div *ngIf="!isSearchingLookup && chemicalSourceFilter === 'custom' && customChemicalsLoaded.length === 0 && !chemicalSearchQuery.trim()" class="text-center py-4">
        <i class="bi bi-person text-muted fs-2rem"></i>
        <p class="text-muted mt-2 mb-0">You haven't added any custom chemicals yet</p>
      </div>

      <!-- No results (with a query active) -->
      <div *ngIf="!isSearchingLookup && filteredLookupChemicals.length === 0 && chemicalSearchQuery.trim() && (chemicalSourceFilter !== 'epa' || chemicalSearchQuery.trim())" class="text-center py-4">
        <i class="bi bi-search text-muted fs-2rem"></i>
        <p class="text-muted mt-2 mb-0">No chemicals found matching "{{ chemicalSearchQuery }}"</p>
      </div>

      <div class="d-flex flex-column gap-2">
        <button type="button" class="lookup-result-item" *ngFor="let chem of filteredLookupChemicals"
             (click)="activeChemicalIndex !== null ? selectLookupChemical(chem) : addChemicalFromLookup(chem)">
          <!-- Row 1: Name + all badges inline -->
          <div class="d-flex align-items-center gap-2 flex-wrap mb-1">
            <span class="lookup-result-name">{{ chem.displayName || chem.name }}</span>
            <span class="badge" [ngClass]="getCategoryBadgeClass(chem.category)">
              {{ getCategoryLabel(chem.category) }}
            </span>
            <span *ngIf="chem.signalWord" class="badge" [ngClass]="getSignalWordClass(chem.signalWord)">
              {{ chem.signalWord }}
            </span>
            <span class="badge" [ngClass]="getSourceBadgeClass(chem.source)">
              {{ getSourceLabel(chem.source) }}
            </span>
          </div>
          <!-- Row 2: Active ingredient -->
          <div class="lookup-result-ingredient">
            {{ chem.activeIngredient }}
          </div>
          <!-- Row 3: Metadata -->
          <div class="lookup-result-meta">
            <span *ngIf="chem.manufacturer !== 'Unknown' && chem.manufacturer !== 'Your Organization'">
              <i class="bi bi-building me-1"></i>{{ chem.manufacturer }}
            </span>
            <span *ngIf="chem.epaNumber !== 'Custom' && chem.epaNumber !== 'N/A'">
              # {{ chem.epaNumber }}
            </span>
            <span *ngIf="chem.formulation">
              <i class="bi bi-capsule me-1"></i>{{ chem.formulation }}
            </span>
            <span *ngIf="chem.defaultRate">
              <i class="bi bi-speedometer me-1"></i>{{ chem.defaultRate }}
            </span>
          </div>
        </button>
      </div>
    </div>
  </div>

  <div class="modal-footer lookup-footer">
    <div class="lookup-footer-count">
      {{ filteredLookupChemicals.length }} chemicals shown
    </div>
    <button type="button" class="btn btn-outline-secondary btn-sm" (click)="closeChemicalLookup()">
      Cancel
    </button>
  </div>
</div>
